<script lang="ts">
		import { format } from 'svelte-i18n';
	//Design system components
	import {
		ActionsContainer,
		AdvancedCard,
		Alert,
		Button,
		Container,
		IconButton,
		Modal,
		type SubtitlePropsTypes,
	} from '@Shelly/shelly-design-system/index';
	//Helpers
	import { devices_status } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { readDeviceStatus, readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { deriveRoomName } from 'src/helpers/service_rooms_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { sortBy } from 'src/components/Lists/list-helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		findDeviceByIP,
		handleDeviceByIP,
	} from 'src/helpers/xt1_helpers/ev_charger/ev_charger_helpers';
	import { isVirtualDevice } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { isPeripheralDevice } from 'src/helpers/addons/addon_device_helpers';
	import SelectList from 'src/components/SelectList/SelectList.svelte';
	//Types
	import type { Device } from 'src/types/Device';

	export let device: Device;
	export let auto_balance: {
		em_ip: string;
		enable: boolean;
		max_current: number;
		exclude_self_current: boolean;
	};

	let showModal = false;

	type EMDevice = {
		id?: string;
		title: string;
		deviceIP: string;
		subtitle: SubtitlePropsTypes;
		avatar: { image?: string; icon?: string };
	};

	const sortListByPosition = (list: any) => {
		return list.sort((x: any, y: any) => {
			return sortBy(x, y, 'position');
		});
	};

	const filterEMDevices = (dev: Device) => {
		const isEMDevice = readDeviceStatus(dev?.id);
		return (
			!dev?.shared &&
			!isVirtualDevice(dev?.type) &&
			!isPeripheralDevice(dev?.type) &&
			(isEMDevice?.['em:0'] || isEMDevice?.['em1:0'])
		);
	};

	const filterDevices: any = (devices: Device[]) => {
		let filteredDevicesByRoom = {};
		devices
			.filter((dev) => filterEMDevices(dev))
			.forEach((dev: Device, _i) => {
				const roomID = dev.room_id;
				const status = devices_status.getByRealId(dev.id);
				const deviceIP = status?.eth?.ip || status?.wifi?.sta_ip || '';
				const offline = status?.online ? '' : `- ${capFirstChar($format('label_offline'))}`;
				const deviceData = {
					name: `${dev.name}${offline}`,
					id: dev.id,
					selected: selectedDevice?.id == dev.id,
					roomName: deriveRoomName(roomID),
					roomID: roomID,
					ssid: dev.ssid,
					image: dev.image ? dev.image : undefined,
					backgroundColor: dev.backgroundColor ? dev.backgroundColor : undefined,
					icon: dev.icon ? dev.icon : undefined,
					descriptiveText:
						dev.ssid != device.ssid
							? $format('webhooks_device_is_not_on_the_same_network', {
									default: 'The device is not on the same Wi-Fi network.',
								})
							: `${dev.ssid} ${deviceIP}`,
				};
				if (filteredDevicesByRoom[roomID]) {
					filteredDevicesByRoom[roomID].cards = [
						...filteredDevicesByRoom[roomID].cards,
						deviceData,
					];
				} else {
					filteredDevicesByRoom[roomID] = {
						cards: [deviceData],
						roomName: deriveRoomName(roomID),
						roomID: roomID,
					};
				}
			});
		return Object.values(filteredDevicesByRoom);
	};

	let linkedDevice: EMDevice = findDeviceByIP(auto_balance.em_ip);
	let selectedDevice: EMDevice = linkedDevice;
	let roomsFiltered = null;

	const updateRoomsFiltered = () => {
		roomsFiltered = sortListByPosition(filterDevices(Object.values($devices)));
	};

	const onClose = () => {
		showModal = false;
		selectedDevice = linkedDevice;
		updateRoomsFiltered();
	};

	updateRoomsFiltered();
</script>

<AdvancedCard {...linkedDevice} isCondensed class="width-md-l width-sm-full mb-s">
	<ActionsContainer orientation="horizontal" type="simple" slot="right">
		<IconButton
			aria-label={$format('button_edit', { default: 'Edit' })}
			icon="fas fa-pen"
			slot="first"
			on:click={() => {
				showModal = true;
			}}
		/>
	</ActionsContainer>
</AdvancedCard>

<Modal
	title={$format('label_select_device', { default: 'Select device' })}
	{showModal}
	on:close={onClose}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		{#if roomsFiltered?.length}
			<div class="pt-s" />
			<SelectList
				title={capFirstChar(
					$format('label_selected', {
						default: 'Selected:',
					}),
				)}
				variant="radio"
				list={roomsFiltered}
				on:click={(e) => {
					selectedDevice = handleDeviceByIP(e.detail);
					roomsFiltered = sortListByPosition(filterDevices(Object.values($devices)));
				}}
			/>
		{:else}
			<Alert
				type="warning"
				class="mb-s"
				heading={$format('no_devices_em_support_heading', {
					default: 'No devices with EM Metering support found',
				})}
				isHeadingBold
			/>
		{/if}
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<Button width="m" variant="secondary" class="mr-s" on:click={onClose}>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			width="m"
			on:click={() => {
				showModal = false;
				linkedDevice = selectedDevice;
				auto_balance.em_ip = selectedDevice.deviceIP;
			}}
		>
			{capFirstChar(
				$format('button_add_device', {
					default: 'add device',
				}),
			)}
		</Button>
	</div>
</Modal>
